import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  useTheme,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  LocationOn,
  CalendarToday,
  CheckCircle,
  ArrowForward,
  Close,
  TrendingUp,
  Public,
  Speed,
  Security,
  Router,
  CellTower,
  Storage,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const Projects = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Updated stats to match home page data
  const stats = [
    { number: '500,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '10,000+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '150+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <CheckCircle />, color: '#191919' },
  ];

  const projects = [
    {
      id: 1,
      name: "Trans-Atlantic Submarine Cable",
      location: "USA to Europe",
      year: "2023",
      status: "Completed",
      progress: 100,
      description: "High-capacity fiber optic cable linking New York to London with 200Tbps capacity, reducing latency by 30% and providing redundant connectivity across the Atlantic.",
      image: "/images/Major Projects.jpg",
      highlights: [
        "200Tbps total capacity",
        "30% latency reduction",
        "100% redundant path",
        "Direct connection to major financial hubs"
      ],
      color: "#e60108"
    },
    {
      id: 2,
      name: "5G Tower Rollout",
      location: "India",
      year: "2024",
      status: "In Progress",
      progress: 65,
      description: "Deploying 2000+ 5G towers across major cities including Mumbai, Delhi, Bangalore, and Chennai, bringing ultra-fast connectivity to 50M+ users.",
      image: "/images/Major Projects.jpg",
      highlights: [
        "2,000+ towers deployed",
        "50M+ users covered",
        "1Gbps+ speeds",
        "Smart city integration"
      ],
      color: "#191919"
    },
    {
      id: 3,
      name: "Hyperscale Data Center",
      location: "Singapore",
      year: "2024",
      status: "Completed",
      progress: 100,
      description: "100MW data center with AI-ready infrastructure, featuring liquid cooling and sustainable energy solutions.",
      image: "/images/DataCenters.jpg",
      highlights: [
        "100MW capacity",
        "AI-optimized design",
        "Liquid cooling",
        "100% renewable energy"
      ],
      color: "#e60108"
    },
    {
      id: 4,
      name: "Smart City Network",
      location: "Dubai",
      year: "2025",
      status: "Planned",
      progress: 25,
      description: "IoT-enabled city-wide connectivity solution with 1,000+ smart sensors and real-time monitoring capabilities.",
      image: "/images/Major Projects.jpg",
      highlights: [
        "1,000+ IoT sensors",
        "Real-time monitoring",
        "Smart traffic management",
        "Public WiFi mesh network"
      ],
      color: "#a0a0a0"
    },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed': return '#00C853';
      case 'In Progress': return '#FFC107';
      case 'Planned': return '#2196F3';
      default: return '#a0a0a0';
    }
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setDialogOpen(true);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Major Projects.jpg"
          alt="Major Projects"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Major Projects"
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 600,
                letterSpacing: '0.5px',
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Major Projects
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Building the future of global communication with cutting-edge infrastructure across 150+ countries
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section - Updated to match home page */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Projects Grid */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Portfolio"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Featured Projects
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Showcasing our commitment to innovation and excellence
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {projects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={project.image}
                        alt={project.name}
                        sx={{ 
                          transition: 'transform 0.5s ease',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${project.color}60 0%, transparent 100%)`,
                        }}
                      />
                      <Chip
                        label={project.status}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          bgcolor: getStatusColor(project.status),
                          color: '#fefefe',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    
                    <CardContent sx={{ flex: 1, p: 3 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                        {project.name}
                      </Typography>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                        <Chip 
                          icon={<LocationOn sx={{ fontSize: 14 }} />} 
                          label={project.location} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Chip 
                          icon={<CalendarToday sx={{ fontSize: 14 }} />} 
                          label={project.year} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Stack>
                      
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 2, lineHeight: 1.6 }}>
                        {project.description.substring(0, 100)}...
                      </Typography>

                      {project.status !== 'Completed' && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#a0a0a0' }}>Progress</Typography>
                            <Typography variant="caption" fontWeight="bold" sx={{ color: project.color }}>{project.progress}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={project.progress} 
                            sx={{ 
                              height: 4, 
                              borderRadius: 2,
                              bgcolor: project.color + '20',
                              '& .MuiLinearProgress-bar': { bgcolor: project.color }
                            }} 
                          />
                        </Box>
                      )}
                      
                      <Button
                        variant="outlined"
                        fullWidth
                        endIcon={<ArrowForward />}
                        onClick={() => handleViewDetails(project)}
                        sx={{ 
                          mt: 2,
                          borderColor: project.color,
                          color: project.color,
                          '&:hover': {
                            borderColor: project.color,
                            bgcolor: project.color + '10',
                          }
                        }}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <CTASection 
        title="Have a Project in Mind?" 
        subtitle="Let's discuss how we can bring your vision to life"
        buttonText="Contact Our Team"
        buttonLink="/contact"
      />

      {/* Project Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        {selectedProject && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <Box sx={{ position: 'relative', height: 250 }}>
                <Box
                  component="img"
                  src={selectedProject.image}
                  alt={selectedProject.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${selectedProject.color}60 0%, transparent 100%)`,
                  }}
                />
                <IconButton
                  onClick={() => setDialogOpen(false)}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fefefe',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <Close />
                </IconButton>
                <Chip
                  label={selectedProject.status}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    bgcolor: getStatusColor(selectedProject.status),
                    color: '#fefefe',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                {selectedProject.name}
              </Typography>
              
              <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                <Chip icon={<LocationOn />} label={selectedProject.location} variant="outlined" />
                <Chip icon={<CalendarToday />} label={selectedProject.year} variant="outlined" />
              </Stack>
              
              <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                {selectedProject.description}
              </Typography>
              
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                Key Highlights
              </Typography>
              
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {selectedProject.highlights.map((highlight, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircle sx={{ fontSize: 16, color: selectedProject.color }} />
                      <Typography variant="body2" sx={{ color: '#191919' }}>{highlight}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setDialogOpen(false);
                  navigate('/contact');
                }}
                sx={{
                  bgcolor: '#e60108',
                  '&:hover': { bgcolor: '#b80006' }
                }}
              >
                Inquire About This Project
              </Button>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Projects;